/**
 * Grid selection → the highlighted lines a brainstorm request carries. Pure —
 * the toolbar action owns the session reads, this owns the shape.
 *
 * WHY COLUMN LABELS APPEAR ONLY SOMETIMES. A one-column selection reads like a
 * passage — the writer selected script (or a translation), and prefixing every
 * line with the same column name would only spend selection budget saying one
 * thing N times. A MULTI-column selection is a table: "Original Script" next to
 * "English" next to "Visual notes", and without the labels the model sees an
 * undifferentiated soup of lines it will happily misattribute ("the English
 * translation says…" about a note). So labels appear exactly when they
 * disambiguate: two or more selected columns.
 *
 * ROW-MAJOR, one line per non-empty cell, both orders as the grid shows them.
 * A row's cells stay adjacent — "line 4 and its translation" is the unit a
 * question is about, not "every line, then every translation".
 */

export interface GridSelectionInput {
  /** Selected row ids, in visual order (the toolbar publishes them so). */
  rowIds: readonly string[];
  /** Selected column ids, in visual order. */
  colIds: readonly string[];
  /** Cell text for one (row, col) — the caller binds the session. */
  textOf: (rowId: string, colId: string) => string;
  /** Column display name, for the multi-column labels. */
  colName: (colId: string) => string;
}

/** The selected cells as lines, ready for `capSelection`. */
export function gridSelectionLines(input: GridSelectionInput): string[] {
  const label = input.colIds.length > 1;
  const lines: string[] = [];
  for (const rowId of input.rowIds) {
    for (const colId of input.colIds) {
      const text = input.textOf(rowId, colId).trim();
      if (text.length === 0) continue;
      if (label) {
        const name = input.colName(colId).trim();
        lines.push(name.length > 0 ? `[${name}] ${text}` : text);
      } else {
        lines.push(text);
      }
    }
  }
  return lines;
}

/** True when the selection holds at least one cell with text to ask about. */
export function gridSelectionHasText(input: GridSelectionInput): boolean {
  for (const rowId of input.rowIds) {
    for (const colId of input.colIds) {
      if (input.textOf(rowId, colId).trim().length > 0) return true;
    }
  }
  return false;
}
